const API_URL = process.env.REACT_APP_BACKEND_URL;

let sessionId = sessionStorage.getItem('analytics_session_id');
let visitorId = localStorage.getItem('analytics_visitor_id');

if (!sessionId) {
  sessionId = 'sess_' + Math.random().toString(36).substr(2, 16);
  sessionStorage.setItem('analytics_session_id', sessionId);
}

interface AnalyticsEvent {
  event_type: string;
  page_url?: string;
  page_title?: string;
  product_id?: string;
  product_name?: string;
  search_query?: string;
  user_id?: string | null;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
}

const trackQueue: AnalyticsEvent[] = [];
let isTracking = false;

const sendEvent = async (eventData: AnalyticsEvent) => {
  try {
    const response = await fetch(`${API_URL}/api/analytics/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Request-ID': crypto.randomUUID(),
      },
      body: JSON.stringify({
        ...eventData,
        session_id: sessionId,
        visitor_id: visitorId,
        referrer: document.referrer || null,
        utm_source: new URLSearchParams(window.location.search).get('utm_source'),
        utm_medium: new URLSearchParams(window.location.search).get('utm_medium'),
        utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign'),
      }),
    });

    if (response.ok) {
      const data = await response.json();

      if (data.visitor_id && !visitorId) {
        visitorId = data.visitor_id;
        localStorage.setItem('analytics_visitor_id', visitorId!);
      }
      return data;
    }
  } catch (error) {
    console.warn('[Analytics] Failed to track event:', error);
    trackQueue.push(eventData);
  }
  return null;
};

const processQueue = async () => {
  if (isTracking || trackQueue.length === 0) return;

  isTracking = true;
  while (trackQueue.length > 0) {
    const event = trackQueue.shift()!;
    await sendEvent(event);
  }
  isTracking = false;
};

setInterval(processQueue, 30000);

const analytics = {
  pageView: (pageTitle?: string) => {
    sendEvent({
      event_type: 'page_view',
      page_url: window.location.href,
      page_title: pageTitle || document.title,
    });
  },

  productView: (productId: string, productName?: string) => {
    sendEvent({
      event_type: 'product_view',
      page_url: window.location.href,
      product_id: productId,
      product_name: productName,
    });
  },

  search: (query: string) => {
    sendEvent({
      event_type: 'search',
      page_url: window.location.href,
      search_query: query,
    });
  },

  addToCart: (productId: string, productName?: string, quantity: number = 1) => {
    sendEvent({
      event_type: 'add_to_cart',
      page_url: window.location.href,
      product_id: productId,
      product_name: productName,
      metadata: { quantity },
    });
  },

  removeFromCart: (productId: string, productName?: string) => {
    sendEvent({
      event_type: 'remove_from_cart',
      page_url: window.location.href,
      product_id: productId,
      product_name: productName,
    });
  },

  checkoutStart: (cartTotal: number, itemCount: number) => {
    sendEvent({
      event_type: 'checkout_start',
      page_url: window.location.href,
      metadata: { cart_total: cartTotal, item_count: itemCount },
    });
  },

  checkoutComplete: (orderId: string, orderTotal: number) => {
    sendEvent({
      event_type: 'checkout_complete',
      page_url: window.location.href,
      metadata: { order_id: orderId, order_total: orderTotal },
    });
  },

  click: (elementName: string, metadata: Record<string, unknown> = {}) => {
    sendEvent({
      event_type: 'click',
      page_url: window.location.href,
      metadata: { element: elementName, ...metadata },
    });
  },

  error: (errorMessage: string, errorStack: string | null = null) => {
    sendEvent({
      event_type: 'error',
      page_url: window.location.href,
      metadata: { error: errorMessage, stack: errorStack },
    });
  },

  login: (userId: string, method: string = 'email') => {
    sendEvent({
      event_type: 'login',
      page_url: window.location.href,
      user_id: userId,
      metadata: { method },
    });
  },

  logout: (userId: string) => {
    sendEvent({
      event_type: 'logout',
      page_url: window.location.href,
      user_id: userId,
    });
  },

  signup: (userId: string, method: string = 'email') => {
    sendEvent({
      event_type: 'signup',
      page_url: window.location.href,
      user_id: userId,
      metadata: { method },
    });
  },

  chatStart: (userId: string | null = null) => {
    sendEvent({
      event_type: 'chat_start',
      page_url: window.location.href,
      user_id: userId,
    });
  },

  chatMessage: (userId: string | null = null, messageType: string = 'user') => {
    sendEvent({
      event_type: 'chat_message',
      page_url: window.location.href,
      user_id: userId,
      metadata: { message_type: messageType },
    });
  },

  setUser: (userId: string) => {
    localStorage.setItem('analytics_user_id', userId);
  },

  clearUser: () => {
    localStorage.removeItem('analytics_user_id');
  },

  heartbeat: () => {
    const userId = localStorage.getItem('analytics_user_id');
    fetch(`${API_URL}/api/analytics/heartbeat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Request-ID': crypto.randomUUID() },
      body: JSON.stringify({
        session_id: sessionId,
        visitor_id: visitorId,
        user_id: userId,
        current_page: window.location.pathname,
      }),
    }).catch(() => {});
  },

  getSessionId: () => sessionId,
  getVisitorId: () => visitorId,
};

setInterval(() => analytics.heartbeat(), 60000);
setTimeout(() => analytics.heartbeat(), 1000);

window.addEventListener('error', (event) => {
  analytics.error(event.message, event.error?.stack);
});

window.addEventListener('unhandledrejection', (event) => {
  analytics.error(`Unhandled Promise Rejection: ${event.reason}`);
});

export default analytics;
